import { API_BASE_URL } from "@/lib/config"
import {
  ApiError,
  AuthError,
  parseRetryAfterMs,
} from "@/lib/api/errors"
import type {
  BookingRequestDTO,
  BookingResponse,
  EventResponse,
  HotelOffersResponse,
  HotelAddOnsResponse,
  FlightOffersResponse,
} from "@/lib/api/types"

export { ApiError, AuthError } from "@/lib/api/errors"

interface HotelOffersQuery {
  eventId: string
  stayStartDate: string
  stayEndDate: string
  passengerCount: number
  shared?: boolean
}

interface FlightOffersQuery {
  eventId: string
  stayStartDate: string
  stayEndDate: string
  passengerCount: number
  departureAirports?: string[]
  destination?: string
  mode?: "hotel_flight" | "flight"
}

async function readJsonBody<T>(response: Response): Promise<T> {
  const text = await response.text()
  if (!text.trim()) {
    throw new ApiError(response.status, {
      bodyMessage: `Expected JSON but got an empty response (status ${response.status})`,
    })
  }

  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError(response.status, {
      bodyMessage: `Expected JSON but got non-JSON response (status ${response.status})`,
    })
  }
}

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  })

  if (!response.ok) {
    const status = response.status

    if (status === 401) {
      throw new AuthError()
    }

    let bodyMessage: string | undefined
    try {
      const data = await readJsonBody<{ message?: unknown }>(response)
      if (typeof data?.message === "string") {
        bodyMessage = data.message
      }
    } catch (error) {
      bodyMessage = error instanceof ApiError ? error.debugInfo ?? undefined : undefined
    }

    const retryAfterMs = parseRetryAfterMs(
      response.headers.get("Retry-After")
    )

    throw new ApiError(status, { bodyMessage, retryAfterMs })
  }

  return readJsonBody<T>(response)
}

export function getEvent(slug: string) {
  return fetchJson<EventResponse>(`/events/${slug}`)
}

export function getHotelOffers({
  eventId,
  stayStartDate,
  stayEndDate,
  passengerCount,
  shared,
}: HotelOffersQuery) {
  const searchParams = new URLSearchParams()
  searchParams.set("stayStartDate", stayStartDate)
  searchParams.set("stayEndDate", stayEndDate)
  searchParams.set("passengerCount", String(passengerCount))

  if (shared === true) {
    searchParams.set("shared", "true")
  }

  return fetchJson<HotelOffersResponse>(
    `/events/${eventId}/offers/hotels?${searchParams.toString()}`
  )
}

export function getHotelAddOns(
  hotelIds: string[],
  stay?: { stayStartDate: string; stayEndDate: string }
) {
  const searchParams = new URLSearchParams()
  hotelIds.forEach((id) => searchParams.append("hotelIds[]", id))

  // Saying which stay this is lets the API leave out add-ons tied to another one.
  if (stay) {
    searchParams.set("stayStartDate", stay.stayStartDate.slice(0, 10))
    searchParams.set("stayEndDate", stay.stayEndDate.slice(0, 10))
  }

  return fetchJson<HotelAddOnsResponse>(`/hotels/addons?${searchParams.toString()}`)
}

export function getFlightOffers({
  eventId,
  stayStartDate,
  stayEndDate,
  passengerCount,
  departureAirports,
  destination,
  mode,
}: FlightOffersQuery) {
  const searchParams = new URLSearchParams()
  searchParams.set("stayStartDate", stayStartDate)
  searchParams.set("stayEndDate", stayEndDate)
  searchParams.set("passengerCount", String(passengerCount))
  departureAirports?.forEach((code) =>
    searchParams.append("departureAirports[]", code)
  )
  if (destination) {
    searchParams.set("destination", destination)
  }
  if (mode) {
    searchParams.set("mode", mode)
  }

  return fetchJson<FlightOffersResponse>(
    `/events/${eventId}/offers/flights?${searchParams.toString()}`
  )
}

export function createBooking(payload: BookingRequestDTO) {
  return fetchJson<BookingResponse>("/bookings", {
    method: "POST",
    body: JSON.stringify(payload),
  })
}
